import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAccount } from '../../hooks/useAccount'
import { useImageUpload } from '../../hooks/useImageUpload'

function AIImageUploadTab() {
  const { t } = useTranslation('upload')
  const navigate = useNavigate()
  const { selectedAccount } = useAccount()
  const fileInputRef = useRef(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const {
    uploadQueue,
    uploadFiles,
    removeFromQueue,
    retryUpload,
    clearCompleted
  } = useImageUpload()

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files)
    if (selectedAccount && files.length > 0) {
      uploadFiles(files, selectedAccount.id)
    }
  }, [selectedAccount, uploadFiles])

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files)
    if (selectedAccount && files.length > 0) {
      uploadFiles(files, selectedAccount.id)
    }
  }, [selectedAccount, uploadFiles])

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const completedCount = uploadQueue.filter(item => item.status === 'complete').length
  const hasCompleted = completedCount > 0

  if (!selectedAccount) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 md:p-12 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noAccount.title')}</h3>
        <p className="text-gray-600">{t('noAccount.messageImages')}</p>
      </div>
    )
  }

  return (
    <div>
      {/* Description */}
      <div className="mb-6">
        <p className="text-sm text-gray-600">
          {t('tabs.aiImage.description')}
        </p>
      </div>

      {/* Brand Settings Hint */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-blue-800">
            {t('tabs.aiImage.brandSettingsHint')
              .split('<link>')
              .map((part, i) =>
                i === 0 ? part : (
                  <>
                    <a
                      href={selectedAccount ? `/accounts/${selectedAccount.id}/brand-settings` : '/settings?tab=brand'}
                      className="font-medium underline hover:text-blue-900"
                    >
                      {part.split('</link>')[0]}
                    </a>
                    {part.split('</link>')[1]}
                  </>
                )
              )}
          </p>
        </div>
      </div>

      {/* Drag and Drop Zone */}
      <div
        className={`relative rounded-lg border-2 border-dashed transition-all ${
          isDragOver
            ? 'border-purple-500 bg-purple-50'
            : 'border-gray-300 bg-white hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="p-6 sm:p-8 md:p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="mt-4 text-lg text-gray-900">
            {t('dropzone.dragImages')}
          </p>
          <p className="mt-2 text-sm text-gray-600">
            {t('dropzone.or')}
          </p>
          <button
            type="button"
            onClick={handleBrowseClick}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            {t('dropzone.browseImages')}
          </button>
          <p className="mt-4 text-xs text-gray-500">
            {t('dropzone.formatsImages')}
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('queue.title')}</h2>
            {hasCompleted && (
              <button
                onClick={clearCompleted}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                {t('queue.clearCompleted', { count: completedCount })}
              </button>
            )}
          </div>

          <div className="space-y-3">
            {uploadQueue.map((item) => (
              <UploadItem
                key={item.id}
                item={item}
                onRemove={() => removeFromQueue(item.id)}
                onRetry={() => retryUpload(item.id, selectedAccount.id)}
              />
            ))}
          </div>

          {hasCompleted && (
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/approval')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                {t('queue.viewApprovalQueue')}
                <svg className="ml-2 -mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function UploadItem({ item, onRemove, onRetry }) {
  const { t } = useTranslation('upload')

  const statusText = {
    pending: t('status.waiting'),
    uploading: t('status.uploading', { progress: item.progress }),
    processing: item.imageData?.status === 'analyzed'
      ? t('status.generatingCaption')
      : t('status.analyzing'),
    complete: item.imageData
      ? t('status.completeWithSentiment', { sentiment: item.imageData.sentiment || 'Unknown' })
      : t('status.uploadComplete'),
    error: item.error || t('status.uploadFailed')
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-start space-x-4">
        {/* Thumbnail */}
        <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
          {item.preview ? (
            <img
              src={item.preview}
              alt={item.file.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {item.file.name}
          </p>
          <p className="text-sm text-gray-500">
            {t('metadata.fileSize', { size: (item.file.size / (1024 * 1024)).toFixed(2) })}
          </p>

          {/* Progress bar */}
          {(item.status === 'uploading' || item.status === 'processing') && (
            <div className="mt-2">
              <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    item.status === 'processing'
                      ? 'bg-purple-500 animate-pulse'
                      : 'bg-blue-500'
                  }`}
                  style={{ width: item.status === 'processing' ? '100%' : `${item.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Status */}
          <div className="mt-2 flex items-center space-x-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              item.status === 'complete' ? 'bg-green-100 text-green-800' :
              item.status === 'error' ? 'bg-red-100 text-red-800' :
              item.status === 'processing' ? 'bg-purple-100 text-purple-800' :
              item.status === 'uploading' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {statusText[item.status]}
            </span>

            {item.status === 'processing' && (
              <span className="text-xs text-gray-500 animate-pulse">
                {t('status.analyzingContent')}
                {item.processingElapsed && (
                  <span className="ml-1">
                    ({Math.floor(item.processingElapsed / 1000)}s)
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Warning message if timeout reached */}
          {item.warning && (
            <div className="mt-2 flex items-start space-x-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
              <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{item.warning}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0">
          {item.status === 'error' && (
            <button
              onClick={onRetry}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              {t('actions.retry')}
            </button>
          )}
          {(item.status === 'pending' || item.status === 'error' || item.status === 'complete') && (
            <button
              onClick={onRemove}
              className="ml-2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AIImageUploadTab
